import { HttpRequestError } from "@nktkas/hyperliquid";

type TradingErrorField = "price" | "amount" | "triggerPrice" | "general";

type TranslationValues = Record<string, string | number>;

type TranslationFn = (key: string, values?: TranslationValues) => string;

export type NormalizedHyperliquidError = {
	code: string;
	field: TradingErrorField;
	rawMessage: string;
	translationKey: string;
	values?: TranslationValues;
};

export type OrderHistoryStatusUi = {
	colorClass: string;
	detailKey?: string;
	labelKey?: string;
};

type ErrorMatcher = {
	code: string;
	field: TradingErrorField;
	matches: (message: string) => boolean;
	translationKey: string;
};

const ORDER_PREFIX_RE = /^Order\s+\d+:\s*/i;

const ERROR_MATCHERS: ErrorMatcher[] = [
	{
		code: "tick",
		field: "price",
		matches: (message) =>
			message.includes("price must be divisible by tick size"),
		translationKey: "order.reason.tick-size",
	},
	{
		code: "minTradeNtl",
		field: "amount",
		matches: (message) =>
			message.includes("order must have minimum value of $10"),
		translationKey: "order.reason.min-trade-ntl",
	},
	{
		code: "minTradeSpotNtl",
		field: "amount",
		matches: (message) =>
			message.includes("order must have minimum value of 10 "),
		translationKey: "order.reason.min-trade-spot-ntl",
	},
	{
		code: "perpMargin",
		field: "general",
		matches: (message) =>
			message.includes("insufficient margin to place order"),
		translationKey: "order.reason.perp-margin",
	},
	{
		code: "reduceOnly",
		field: "general",
		matches: (message) =>
			message.includes("reduce only order would increase position"),
		translationKey: "order.reason.reduce-only",
	},
	{
		code: "badAloPx",
		field: "price",
		matches: (message) =>
			message.includes("post only order would have immediately matched"),
		translationKey: "order.reason.bad-alo-px",
	},
	{
		code: "iocCancel",
		field: "general",
		matches: (message) =>
			message.includes(
				"order could not immediately match against any resting orders",
			),
		translationKey: "order.reason.ioc-cancel",
	},
	{
		code: "badTriggerPx",
		field: "triggerPrice",
		matches: (message) => message.includes("invalid tp/sl price"),
		translationKey: "order.reason.bad-trigger-px",
	},
	{
		code: "marketOrderNoLiquidity",
		field: "general",
		matches: (message) =>
			message.includes("no liquidity available for market order"),
		translationKey: "order.reason.market-order-no-liquidity",
	},
	{
		code: "positionIncreaseAtOpenInterestCap",
		field: "general",
		matches: (message) =>
			message.includes(
				"order would increase open interest while open interest is capped",
			),
		translationKey: "order.reason.open-interest-cap",
	},
	{
		code: "tooAggressiveAtOpenInterestCap",
		field: "price",
		matches: (message) =>
			message.includes(
				"order rejected due to price more aggressive than oracle while at open interest cap",
			),
		translationKey: "order.reason.too-aggressive-at-open-interest-cap",
	},
	{
		code: "openInterestIncrease",
		field: "general",
		matches: (message) =>
			message.includes("order would increase open interest too quickly"),
		translationKey: "order.reason.open-interest-increase",
	},
	{
		code: "insufficientSpotBalance",
		field: "amount",
		matches: (message) =>
			message.includes("order has insufficient spot balance to trade"),
		translationKey: "order.reason.insufficient-spot-balance",
	},
	{
		code: "withdrawalInsufficientBalance",
		field: "amount",
		matches: (message) =>
			message.includes("insufficient balance for withdrawal"),
		translationKey: "transferWallet.insufficient-balance",
	},
	{
		code: "oracle",
		field: "price",
		matches: (message) => message.includes("order price too far from oracle"),
		translationKey: "order.reason.oracle",
	},
	{
		code: "perpMaxPosition",
		field: "general",
		matches: (message) =>
			message.includes(
				"order would cause position to exceed margin tier limit at current leverage",
			),
		translationKey: "order.reason.perp-max-position",
	},
	{
		code: "missingOrder",
		field: "general",
		matches: (message) =>
			message.includes("order was never placed, already canceled, or filled"),
		translationKey: "order.reason.missing-order",
	},
	{
		code: "leverageInsufficientMargin",
		field: "general",
		matches: (message) =>
			message.includes(
				"does not have sufficient margin available to decrease leverage",
			),
		translationKey: "leverage.reason.insufficient-margin-decrease",
	},
	{
		code: "leverageExceedsMaxPosition",
		field: "general",
		matches: (message) =>
			message.includes(
				"position size exceeds maximum allowed at requested leverage",
			),
		translationKey: "leverage.reason.exceeds-max-position",
	},
	{
		// HL refuses account-type switches while account state conflicts with
		// re-pooling collateral (open positions / orders — exact rule is not
		// documented; HL's own app treats this response as a terminal outcome).
		code: "abstractionTransition",
		field: "general",
		matches: (message) =>
			message.includes("abstraction transition not allowed"),
		translationKey: "account-type.reason.transition-not-allowed",
	},
];

const ORDER_HISTORY_STATUS_UI: Record<string, OrderHistoryStatusUi> = {
	open: {
		colorClass: "text-trade-text-secondary",
		labelKey: "order.status.open",
	},
	filled: {
		colorClass: "text-trade-green",
		labelKey: "order.status.filled",
	},
	canceled: {
		colorClass: "text-trade-red",
		labelKey: "order.status.canceled",
	},
	triggered: {
		colorClass: "text-trade-text-secondary",
		labelKey: "order.status.triggered",
	},
	rejected: {
		colorClass: "text-trade-red",
		labelKey: "order.status.rejected",
	},
	marginCanceled: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.margin-canceled",
		labelKey: "order.status.margin-canceled",
	},
	vaultWithdrawalCanceled: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.vault-withdrawal-canceled",
		labelKey: "order.status.canceled",
	},
	openInterestCapCanceled: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.open-interest-cap-canceled",
		labelKey: "order.status.canceled",
	},
	selfTradeCanceled: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.self-trade-canceled",
		labelKey: "order.status.canceled",
	},
	reduceOnlyCanceled: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.reduce-only-canceled",
		labelKey: "order.status.canceled",
	},
	siblingFilledCanceled: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.sibling-filled-canceled",
		labelKey: "order.status.canceled",
	},
	delistedCanceled: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.delisted-canceled",
		labelKey: "order.status.canceled",
	},
	liquidatedCanceled: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.liquidated-canceled",
		labelKey: "order.status.canceled",
	},
	scheduledCancel: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.scheduled-cancel",
		labelKey: "order.status.canceled",
	},
	tickRejected: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.tick-size",
		labelKey: "order.status.rejected",
	},
	minTradeNtlRejected: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.min-trade-ntl",
		labelKey: "order.status.rejected",
	},
	perpMarginRejected: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.perp-margin",
		labelKey: "order.status.rejected",
	},
	reduceOnlyRejected: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.reduce-only",
		labelKey: "order.status.rejected",
	},
	badAloPxRejected: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.bad-alo-px",
		labelKey: "order.status.rejected",
	},
	iocCancelRejected: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.ioc-cancel",
		labelKey: "order.status.rejected",
	},
	badTriggerPxRejected: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.bad-trigger-px",
		labelKey: "order.status.rejected",
	},
	marketOrderNoLiquidityRejected: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.market-order-no-liquidity",
		labelKey: "order.status.rejected",
	},
	positionIncreaseAtOpenInterestCapRejected: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.open-interest-cap",
		labelKey: "order.status.rejected",
	},
	positionFlipAtOpenInterestCapRejected: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.open-interest-cap",
		labelKey: "order.status.rejected",
	},
	tooAggressiveAtOpenInterestCapRejected: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.too-aggressive-at-open-interest-cap",
		labelKey: "order.status.rejected",
	},
	openInterestIncreaseRejected: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.open-interest-increase",
		labelKey: "order.status.rejected",
	},
	insufficientSpotBalanceRejected: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.insufficient-spot-balance",
		labelKey: "order.status.rejected",
	},
	oracleRejected: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.oracle",
		labelKey: "order.status.rejected",
	},
	perpMaxPositionRejected: {
		colorClass: "text-trade-red",
		detailKey: "order.reason.perp-max-position",
		labelKey: "order.status.rejected",
	},
};

function extractErrorMessage(error: unknown): string | null {
	if (error instanceof Error && error.message) {
		return error.message;
	}
	if (typeof error === "string" && error) {
		return error;
	}
	return null;
}

function normalizeErrorMessage(message: string): string {
	return message.replace(ORDER_PREFIX_RE, "").trim().toLowerCase();
}

export function normalizeHyperliquidError(
	error: unknown,
): NormalizedHyperliquidError | null {
	const rawMessage = extractErrorMessage(error);
	if (!rawMessage) return null;

	const message = normalizeErrorMessage(rawMessage);
	const match = ERROR_MATCHERS.find((item) => item.matches(message));
	if (!match) return null;

	return {
		code: match.code,
		field: match.field,
		rawMessage,
		translationKey: match.translationKey,
	};
}

/**
 * True when `error` is a known Hyperliquid business-logic rejection
 * (reduce-only would increase position, min-notional, insufficient margin,
 * post-only would cross, …). These are expected user/market outcomes — the
 * mutation's local `onError` already surfaces a translated toast — so callers
 * (e.g. the global Sentry capture in `root-provider.tsx`) can drop them as
 * noise. Unknown rejections return `false` and are still reported. Mirrors
 * {@link isRateLimitError}.
 */
export function isKnownHyperliquidRejection(error: unknown): boolean {
	return normalizeHyperliquidError(error) !== null;
}

export function getHyperliquidErrorMessage(
	t: TranslationFn,
	error: unknown,
	fallbackMessage: string,
): string {
	const normalized = normalizeHyperliquidError(error);
	if (normalized) {
		return t(normalized.translationKey, normalized.values);
	}
	const raw = extractErrorMessage(error);
	if (raw) {
		console.warn("[hyperliquid-error] unmatched:", raw);
	}
	return fallbackMessage;
}

export function isRateLimitError(error: unknown): boolean {
	if (error instanceof HttpRequestError && error.response?.status === 429) {
		return true;
	}
	if (error instanceof Error && /^429\b/.test(error.message)) {
		return true;
	}
	return false;
}

/**
 * A WS subscribe/unsubscribe request that was in flight when the socket dropped
 * (sleep/wake, network blip, HL idle cycling). The SDK's ReconnectingWebSocket
 * + `resubscribe: true` auto-recover, so this is benign, self-healing noise.
 * Message is the SDK's fixed literal from `_postRequest.ts` `handleClose`.
 */
export function isWebSocketClosedError(error: unknown): boolean {
	return (
		error instanceof Error && error.message === "WebSocket connection closed"
	);
}

export function getOrderHistoryStatusUi(status: string): OrderHistoryStatusUi {
	return (
		ORDER_HISTORY_STATUS_UI[status] ?? {
			colorClass:
				status === "filled"
					? "text-trade-green"
					: status === "open" || status === "triggered"
						? "text-trade-text-secondary"
						: "text-trade-red",
		}
	);
}
